import { useState } from "react";
import { XStack, Text, View } from "tamagui";
import { RiArrowDownSLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";
import type { SupplierIdentity } from "@utils/store/cart/useOrder";
import { toTelHref } from "@utils/func/supplierIdentity";

/**
 * 下單流程版頭的「賣方身分」。
 *
 * 為什麼有：交易對象是瓦斯行（平台只做媒合/代收），客戶要查得到跟誰買、統編是什麼、
 * 出事打給誰。但這是「需要時查得到」的資訊，不是下單主動線——所以版頭只留瓦斯行名稱，
 * 統編與電話收進點名稱才展開的小卡，不讓三段小字全程佔著標題列、搶掉標題與地址的重量。
 *
 * 樣式對齊 KioskHeader 既有的 subtitle token（12px / rgba(255,255,255,0.82)），
 * 用在藍色漸層 header 上，故收合態的顏色寫死白系半透明；展開的小卡落在頁面內容之上，
 * 改用白底深字才讀得到。
 * 缺漏處理：名稱是必要條件（呼叫端在 identity 為 null 時整段不渲染）；
 * 統編未帶（後端 bootstrap 尚未提供）或電話未帶則各自省略該段，不留佔位；
 * 兩者都沒有時不顯示展開箭頭，點了也不會開一張空卡。
 *
 * 小卡用原生 div 寫、絕對定位在名稱下方：Tamagui／react-native-web 會過濾 style，
 * position 這類非 RN 合法值會被丟掉。（標題列的漸層遮罩已改畫在 ::before，不會裁到這張卡。）
 */
/**
 * @param align 這一條在版面裡靠哪邊。版頭（PageWrapper）是靠右，Kiosk 首頁把它收在
 *   標題底下的左欄所以靠左。展開的小卡要跟著同一邊錨定——卡片是絕對定位的，
 *   靠左的條配 right:0 的卡，卡片會往左長出螢幕外（只剩右半截看得到）。
 */
const SupplierIdentityBar = ({ identity, align = "end" }: { identity: SupplierIdentity; align?: "start" | "end" }) => {
  // 淡霧頁首：白字讀不到，改用同一族的灰藍
  const haze = useHazePage();
  const { name, taxId, phone } = identity;
  const [open, setOpen] = useState(false);
  const hasDetail = !!(taxId || phone);

  return (
    <div style={{ minWidth: 0, display: "flex", justifyContent: align === "start" ? "flex-start" : "flex-end", position: "relative" }}>
      <XStack
        ai="center"
        gap={3}
        minWidth={0}
        cursor={hasDetail ? "pointer" : "default"}
        pressStyle={hasDetail ? { opacity: 0.6 } : undefined}
        onPress={hasDetail ? () => setOpen((v) => !v) : undefined}>
        <Text color={haze ? v2.hazeSub : "rgba(255,255,255,0.82)"} fontSize={12} fontWeight="500" flexShrink={1} numberOfLines={1}>
          {name}
        </Text>
        {hasDetail && (
          <View style={{ transform: open ? "rotate(180deg)" : undefined, display: "flex" }}>
            <RiArrowDownSLine size={14} color={haze ? v2.hazeSub : "rgba(255,255,255,0.7)"} />
          </View>
        )}
      </XStack>

      {open && hasDetail && (
        <>
          {/* 點卡片以外的地方關閉。蓋滿視窗但透明，不改變畫面外觀。 */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div
            style={{
              position: "absolute",
              top: "100%",
              ...(align === "start" ? { left: 0 } : { right: 0 }),
              marginTop: 8,
              zIndex: 50,
              minWidth: 200,
              background: "#fff",
              borderRadius: 14,
              padding: "12px 14px",
              boxShadow: "0 8px 24px -8px rgba(21, 101, 192, 0.35)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              fontFamily: "inherit",
            }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>{name}</span>
              {taxId && (
                <span style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12 }}>
                  <span style={{ color: "#8A94A6" }}>統編</span>
                  <span style={{ color: "#374151" }}>{taxId}</span>
                </span>
              )}
              {phone && (
                <span style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12 }}>
                  <span style={{ color: "#8A94A6" }}>電話</span>
                  {/* 取消或退換須直接聯繫瓦斯行 → 可直接撥號 */}
                  <a
                    href={toTelHref(phone)}
                    style={{ color: colors.primary, fontWeight: 600, textDecoration: "none" }}>
                    {phone}
                  </a>
                </span>
              )}
          </div>
        </>
      )}
    </div>
  );
};

export default SupplierIdentityBar;
